/**
 * Academy LMS pages — Phases 2/3/4:
 * - AcademyHomePage: dashboard with progress rings, next lesson, announcements
 * - TranscriptPage: unofficial transcript + issue official + history
 * - BadgesPage: badge catalog + earned status
 * - RolesPage: 16 academic roles (read-only reference)
 * - PracticumInboxPage: admin queue for supervisor sign-off
 * - IntegrityCasesPage: admin queue for academic integrity workflow
 * - QaCenterPage: self-audit curriculum/quality snapshot
 * - VerifyPage: public transcript verification lookup
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import * as A from "./lib/api";

const H1 = ({ children }) => <h1 className="font-display text-4xl mb-2">{children}</h1>;
const H2 = ({ children }) => <h2 className="font-display text-2xl mb-3">{children}</h2>;
const Sub = ({ children }) => <p className="mb-6" style={{color:'var(--muted)'}}>{children}</p>;
const Card = ({ children, className = "" }) => <div className={`card p-4 ${className}`}>{children}</div>;
const ProgressBar = ({ pct = 0 }) => (
  <div className="w-full bg-[color:var(--surface-2)] rounded-full h-1.5">
    <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: 'var(--gold)' }} />
  </div>
);

const Ring = ({ pct = 0, size = 80 }) => {
  const r = size/2 - 6;
  const c = 2 * Math.PI * r;
  const off = c - (pct/100) * c;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} stroke="var(--surface-2)" strokeWidth="6" fill="none"/>
      <circle cx={size/2} cy={size/2} r={r} stroke="var(--gold)" strokeWidth="6" fill="none"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}/>
      <text x="50%" y="52%" textAnchor="middle" fontSize="14" fill="var(--text)" fontWeight="600">{pct}%</text>
    </svg>
  );
};

// ==================== Academy Home Dashboard ====================
export function AcademyHomePage() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    A.academyHome().then(r => setD(r.data)).catch(() => {});
    A.academyStatus().then(r => setStatus(r.data)).catch(() => {});
  }, []);
  if (!d) return <div>Loading…</div>;
  return (
    <div data-testid="academy-home-page">
      <div className="flex justify-between items-start mb-4">
        <div>
          <H1>Welcome back, {d.learner?.display_name?.split(' ')[0] || 'Learner'}</H1>
          <Sub>Your academic home. {d.totals.completed_courses} of {d.totals.total_courses} courses complete · {d.totals.enrolled} currently enrolled.</Sub>
        </div>
        <Link to="/academy" className="btn-outline text-sm" data-testid="browse-catalog-btn">Browse full catalog →</Link>
      </div>

      {status && (
        <div className="card p-3 mb-4 flex items-center justify-between" data-testid="home-status-mini">
          <div className="text-xs" style={{color:'var(--muted)'}}>
            <span className="label-caps">Status:</span> {status.status_label} · Effective {status.effective_date}
          </div>
          <Link to="/academy/qa" className="text-xs" style={{color:'var(--gold-hover)'}}>QA Center →</Link>
        </div>
      )}

      {d.next_lesson && (
        <Card className="mb-6" data-testid="next-lesson-card">
          <div className="label-caps mb-1">Continue where you left off</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-lg">{d.next_lesson.lesson_title}</div>
              <div className="text-xs" style={{color:'var(--muted)'}}>in {d.next_lesson.course_title}</div>
            </div>
            <Link to={`/academy/lesson/${d.next_lesson.lesson_id}`} className="btn-gold" data-testid="continue-lesson-btn">Continue →</Link>
          </div>
        </Card>
      )}

      <H2>Enrolled courses</H2>
      {d.enrolled_courses.length === 0 ? (
        <div className="text-sm mb-6" style={{color:'var(--muted)'}}>No enrolled courses yet. <Link to="/academy" className="underline">Browse the catalog</Link> to start a program.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {d.enrolled_courses.map(c => (
            <Link key={c.course_id} to={`/academy/${c.course_id}`} className="card p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform" data-testid={`enrolled-course-${c.course_id}`}>
              <Ring pct={c.pct} size={64}/>
              <div className="flex-1 min-w-0">
                <div className="chip chip-gold text-xs mb-1 truncate">{c.section}</div>
                <div className="font-display text-base truncate">{c.title}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>{c.completed}/{c.lesson_count} lessons</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="recent-activity-card">
          <div className="label-caps mb-2">Recent activity</div>
          {d.recent_activity.length === 0 ? <div className="text-sm" style={{color:'var(--muted)'}}>No activity yet.</div> :
            <ul className="text-sm space-y-1">
              {d.recent_activity.map((r, i) => (
                <li key={i} className="flex justify-between">
                  <span>{r.completed ? '✓' : '○'} {r.lesson_title} <span className="text-xs" style={{color:'var(--muted)'}}>({r.course_title})</span></span>
                  <span className="text-xs font-mono" style={{color:'var(--muted)'}}>{(r.at||'').slice(0,10)}</span>
                </li>
              ))}
            </ul>}
        </Card>
        <Card data-testid="announcements-card">
          <div className="label-caps mb-2">Announcements</div>
          {d.announcements.length === 0 ? <div className="text-sm" style={{color:'var(--muted)'}}>No academic announcements at this time.</div> :
            <ul className="space-y-2">{d.announcements.map(a => (
              <li key={a.id}><div className="font-medium text-sm">{a.title}</div><div className="text-xs" style={{color:'var(--muted)'}}>{a.created_at?.slice(0,10)}</div></li>
            ))}</ul>}
        </Card>
      </div>

      <div className="flex gap-2 mt-6">
        <Link to="/academy/transcript" className="btn-outline text-xs" data-testid="link-transcript">Transcript</Link>
        <Link to="/academy/badges" className="btn-outline text-xs" data-testid="link-badges">Badges</Link>
        <Link to="/academy/roles" className="btn-outline text-xs" data-testid="link-roles">Academic Roles</Link>
        <Link to="/academy/qa" className="btn-outline text-xs" data-testid="link-qa">QA Center</Link>
      </div>
    </div>
  );
}

// ==================== Transcript ====================
export function TranscriptPage() {
  const [d, setD] = useState(null);
  const [history, setHistory] = useState([]);
  const [issuedTo, setIssuedTo] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    A.academyTranscript().then(r => setD(r.data)).catch(() => {});
    A.academyTranscriptHistory().then(r => setHistory(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const issue = async () => {
    setBusy(true);
    try {
      const r = await A.academyTranscriptIssue(issuedTo || "self");
      toast.success(`Official transcript issued. Verification code: ${r.data.verification_code}`);
      setIssuedTo("");
      load();
    } catch { toast.error("Failed to issue transcript"); }
    finally { setBusy(false); }
  };
  if (!d) return <div>Loading…</div>;
  return (
    <div data-testid="transcript-page">
      <H1>Academic Transcript</H1><Sub>Unofficial record. Click "Issue official" to generate a signed, verifiable transcript.</Sub>
      <div className="banner-warning mb-4 text-sm" data-testid="transcript-disclaimer">{d.status_disclaimer}</div>

      <Card className="mb-6" data-testid="transcript-totals">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Enrolled", d.totals.courses_enrolled],
            ["Courses completed", `${d.totals.courses_completed}/${d.totals.courses_total}`],
            ["Lessons completed", `${d.totals.lessons_completed}/${d.totals.lessons_total}`],
            ["Overall", `${d.totals.overall_pct}%`],
          ].map(([k,v],i) => (
            <div key={i}><div className="label-caps text-xs">{k}</div><div className="font-display text-2xl">{v}</div></div>
          ))}
        </div>
      </Card>

      <H2>Program credentials</H2>
      <div className="grid md:grid-cols-2 gap-3 mb-6" data-testid="transcript-programs">
        {d.programs.map(p => (
          <div key={p.program_id} className="card p-3 flex justify-between items-center">
            <div>
              <div className="font-display text-base">{p.program_title}</div>
              <div className="text-xs" style={{color:'var(--muted)'}}>{p.credential}</div>
            </div>
            <div className="text-right">
              <div className="text-xs">{p.courses_completed}/{p.courses_total} · {p.completed_pct}%</div>
              {p.badge_earned && <span className="chip chip-green text-xs">Earned</span>}
            </div>
          </div>
        ))}
      </div>

      <H2>Course record</H2>
      <div className="overflow-x-auto mb-6"><table className="text-sm w-full" data-testid="transcript-courses-table">
        <thead><tr className="text-left">
          <th className="py-1 pr-2 label-caps">#</th>
          <th className="py-1 pr-2 label-caps">Course</th>
          <th className="py-1 pr-2 label-caps">Program</th>
          <th className="py-1 pr-2 label-caps">Status</th>
          <th className="py-1 pr-2 label-caps">Progress</th>
          <th className="py-1 pr-2 label-caps">Version</th>
        </tr></thead>
        <tbody>{d.courses.map(c => (
          <tr key={c.course_id} className="border-t" style={{borderColor:'var(--border)'}}>
            <td className="py-1 pr-2 font-mono text-xs">{c.course_number || '—'}</td>
            <td className="py-1 pr-2">{c.course_title}</td>
            <td className="py-1 pr-2 text-xs" style={{color:'var(--muted)'}}>{c.section}</td>
            <td className="py-1 pr-2"><span className={`chip text-xs ${c.status==='Completed'?'chip-green':c.status==='In Progress'?'chip-gold':'chip-gray'}`}>{c.status}</span></td>
            <td className="py-1 pr-2 font-mono text-xs">{c.completed_lessons}/{c.lesson_count} · {c.completed_pct}%</td>
            <td className="py-1 pr-2 font-mono text-xs">{c.content_version || '—'}</td>
          </tr>
        ))}</tbody>
      </table></div>

      <Card className="mb-6" data-testid="transcript-issue-card">
        <div className="label-caps mb-2">Issue an official transcript</div>
        <div className="flex gap-2">
          <input className="input flex-1" placeholder="Recipient (name of school, employer, or 'self')"
            value={issuedTo} onChange={e=>setIssuedTo(e.target.value)}
            style={{border: '1px solid var(--border)', borderRadius: 4, padding: '4px 8px', background:'transparent'}}
            data-testid="transcript-issued-to"/>
          <button className="btn-primary" disabled={busy} onClick={issue} data-testid="transcript-issue-btn">
            {busy ? 'Issuing…' : 'Issue official'}
          </button>
        </div>
      </Card>

      <H2>Issued transcripts</H2>
      {history.length === 0 ? <div className="text-sm" style={{color:'var(--muted)'}}>No official transcripts have been issued yet.</div> :
        <div className="space-y-2" data-testid="transcript-history">
          {history.map(h => (
            <div key={h.id} className="card p-3 flex justify-between items-center">
              <div>
                <div className="font-mono text-xs">Verification: {h.verification_code}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>Issued to {h.issued_to} on {h.issued_at?.slice(0,10)}</div>
                {h.revoked && <div className="chip chip-amber text-xs mt-1">Revoked: {h.revoked_reason}</div>}
              </div>
              <div className="text-xs font-mono" style={{color:'var(--muted)'}}>hash {(h.content_hash||'').slice(0,10)}…</div>
            </div>
          ))}
        </div>}
    </div>
  );
}

// ==================== Verify (public) ====================
export function VerifyTranscriptPage() {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try { const r = await A.academyTranscriptVerify(code.trim().toUpperCase()); setRes(r.data); }
    catch (e) { setRes({ valid: false, message: "Verification lookup failed." }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="verify-page" className="max-w-2xl mx-auto">
      <H1>Verify Transcript</H1><Sub>Enter a 12-character verification code (e.g. ABCD-EFGH-JKLM) to check if a transcript is genuine and current.</Sub>
      <Card>
        <div className="flex gap-2">
          <input className="input flex-1 font-mono" placeholder="ABCD-EFGH-JKLM" value={code}
            onChange={e=>setCode(e.target.value)}
            style={{border: '1px solid var(--border)', borderRadius: 4, padding: '4px 8px', background:'transparent'}}
            data-testid="verify-code-input"/>
          <button className="btn-primary" disabled={busy || !code.trim()} onClick={check} data-testid="verify-submit">
            {busy?'Checking…':'Verify'}
          </button>
        </div>
      </Card>
      {res && (
        <Card className={`mt-4 ${res.valid?'border-green-700':'border-amber-700'}`} data-testid="verify-result">
          <div className="label-caps mb-2">{res.valid ? '✓ Valid transcript' : '✗ Not valid'}</div>
          {res.message && <div className="text-sm">{res.message}</div>}
          {res.valid && (
            <div className="text-sm space-y-1">
              <div><b>Learner:</b> {res.learner_display_name}</div>
              <div><b>Issued to:</b> {res.issued_to}</div>
              <div><b>Issued at:</b> {res.issued_at?.slice(0,10)}</div>
              <div><b>Courses:</b> {res.courses_completed}/{res.courses_total} completed</div>
              {res.programs_earned?.length > 0 && <div><b>Programs earned:</b> {res.programs_earned.join(', ')}</div>}
              <div className="text-xs font-mono" style={{color:'var(--muted)'}}>hash {res.content_hash?.slice(0,16)}…</div>
              <div className="text-xs mt-2" style={{color:'var(--muted)'}}>{res.status_disclaimer}</div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ==================== Badges ====================
export function BadgesPage() {
  const [b, setB] = useState([]);
  useEffect(() => { A.academyBadges().then(r=>setB(r.data)).catch(()=>{}); }, []);
  return (
    <div data-testid="badges-page">
      <H1>Digital Badges</H1><Sub>Badges are awarded for full completion of every course in a program. They are internal, verifiable, and do not imply licensure or accreditation.</Sub>
      <div className="grid md:grid-cols-3 gap-4">
        {b.map(x => (
          <Card key={x.code} className={`text-center ${x.earned?'':'opacity-60'}`} data-testid={`badge-${x.code}`}>
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3"
              style={{background: x.earned?'var(--gold)':'var(--surface-2)', color: x.earned?'#1a1a1a':'var(--muted)'}}>
              {x.earned ? '★' : '○'}
            </div>
            <div className="font-display text-lg">{x.label}</div>
            <div className="text-xs mt-1" style={{color:'var(--muted)'}}>{x.criteria}</div>
            <div className={`chip mt-2 text-xs ${x.earned?'chip-green':'chip-gray'}`}>{x.earned?'Earned':'Not yet earned'}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ==================== Roles ====================
export function AcademicRolesPage() {
  const [r, setR] = useState([]);
  useEffect(() => { A.academyRoles().then(x=>setR(x.data)).catch(()=>{}); }, []);
  return (
    <div data-testid="roles-page">
      <H1>Academic Roles</H1>
      <Sub>Academic-role permissions are for Academy operations only. They do NOT grant trustee, council, entity, banking, investment, beneficiary, property, insurance, or records authority.</Sub>
      <div className="grid md:grid-cols-2 gap-3">
        {r.map(role => (
          <Card key={role.code} data-testid={`role-${role.code}`}>
            <div className="font-display text-base">{role.label}</div>
            <div className="text-sm mt-1" style={{color:'var(--muted)'}}>{role.purpose}</div>
            <div className="mt-2 text-xs font-mono flex flex-wrap gap-1">
              {(role.grants||[]).map(g => <span key={g} className="chip chip-gold text-xs">{g}</span>)}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ==================== Practicum Inbox (admin) ====================
export function PracticumInboxPage() {
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState({});
  const load = () => A.academyPracticumInbox().then(r=>setRows(r.data)).catch(()=>toast.error("Admin only"));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const decide = async (sid, decision) => {
    try {
      await A.academyPracticumDecision(sid, { decision, note: note[sid] || "" });
      toast.success(`Marked ${decision}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="practicum-inbox-page">
      <H1>Practicum Inbox</H1><Sub>Supervisor sign-off queue for evidence submissions. Decisions are audited.</Sub>
      {rows.length === 0 ? <div className="text-sm" style={{color:'var(--muted)'}}>No submissions.</div> :
        <div className="space-y-3">{rows.map(r => (
          <Card key={r.id} data-testid={`practicum-row-${r.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display text-base">{r.title}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>{r.learner_display_name} · lesson {r.lesson_title} · {r.kind}</div>
              </div>
              <span className={`chip text-xs ${r.status==='approved'?'chip-green':r.status==='needs_revision'||r.status==='rejected'?'chip-amber':'chip-gold'}`}>{r.status}</span>
            </div>
            {r.body_html && <div className="mt-2 text-sm prose max-w-none" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(r.body_html)}}/>}
            {r.supervisor_signoff && <div className="text-xs mt-2 p-2 bg-[color:var(--surface-2)] rounded">
              <b>Signoff by {r.supervisor_signoff.email}:</b> {r.supervisor_signoff.decision} — {r.supervisor_signoff.note}
            </div>}
            <div className="mt-3 flex gap-2 items-center flex-wrap">
              <input placeholder="Supervisor note (optional)" className="input flex-1"
                style={{border: '1px solid var(--border)', borderRadius: 4, padding: '4px 8px', background:'transparent', minWidth: 200}}
                value={note[r.id]||""} onChange={e=>setNote(n=>({...n,[r.id]:e.target.value}))}
                data-testid={`practicum-note-${r.id}`}/>
              <button className="btn-outline text-xs" onClick={()=>decide(r.id,'under_review')} data-testid={`practicum-review-${r.id}`}>Under review</button>
              <button className="btn-outline text-xs" onClick={()=>decide(r.id,'needs_revision')} data-testid={`practicum-revise-${r.id}`}>Needs revision</button>
              <button className="btn-primary text-xs" onClick={()=>decide(r.id,'approved')} data-testid={`practicum-approve-${r.id}`}>Approve</button>
              <button className="btn-outline text-xs" onClick={()=>decide(r.id,'rejected')} data-testid={`practicum-reject-${r.id}`}>Reject</button>
            </div>
          </Card>
        ))}</div>}
    </div>
  );
}

// ==================== Integrity Cases (admin) ====================
export function IntegrityCasesPage() {
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState({});
  const load = () => A.academyIntegrityCases().then(r=>setRows(r.data)).catch(()=>toast.error("Admin only"));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const act = async (cid, action) => {
    try {
      await A.academyIntegrityAction(cid, { action, note: note[cid] || "" });
      toast.success(`Recorded ${action}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="integrity-cases-page">
      <H1>Academic Integrity Cases</H1><Sub>Reviewer queue. Every action is recorded; automated flags are never final findings.</Sub>
      {rows.length === 0 ? <div className="text-sm" style={{color:'var(--muted)'}}>No cases filed.</div> :
        <div className="space-y-3">{rows.map(r => (
          <Card key={r.id} data-testid={`case-${r.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="label-caps text-xs">{r.category}</div>
                <div className="text-sm font-medium">Subject: {r.subject_email}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>Reported by {r.reporter_display}</div>
              </div>
              <span className={`chip text-xs ${r.status==='sanctioned'?'chip-amber':r.status==='dismissed'||r.status==='restored'?'chip-green':'chip-gold'}`}>{r.status}</span>
            </div>
            <div className="mt-2 text-sm">{r.description}</div>
            {(r.reviewer_notes||[]).map((n,i)=>(
              <div key={i} className="text-xs mt-1 p-2 bg-[color:var(--surface-2)] rounded">
                <b>{n.action}</b> by {n.by} — {n.note}
              </div>
            ))}
            <div className="mt-3 flex gap-2 items-center flex-wrap">
              <input placeholder="Reviewer note" className="input flex-1"
                style={{border: '1px solid var(--border)', borderRadius: 4, padding: '4px 8px', background:'transparent', minWidth: 200}}
                value={note[r.id]||""} onChange={e=>setNote(n=>({...n,[r.id]:e.target.value}))}
                data-testid={`case-note-${r.id}`}/>
              <button className="btn-outline text-xs" onClick={()=>act(r.id,'under_review')} data-testid={`case-review-${r.id}`}>Under review</button>
              <button className="btn-outline text-xs" onClick={()=>act(r.id,'dismissed')} data-testid={`case-dismiss-${r.id}`}>Dismiss</button>
              <button className="btn-outline text-xs" onClick={()=>act(r.id,'sanctioned')} data-testid={`case-sanction-${r.id}`}>Sanction</button>
              <button className="btn-outline text-xs" onClick={()=>act(r.id,'restored')} data-testid={`case-restore-${r.id}`}>Restore</button>
            </div>
          </Card>
        ))}</div>}
    </div>
  );
}

// ==================== QA & Accreditation Center ====================
export function QaCenterPage() {
  const [s, setS] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [openPolicy, setOpenPolicy] = useState(null);
  useEffect(() => {
    A.academyQaSnapshot().then(r=>setS(r.data)).catch(()=>{});
    A.academyAnalytics().then(r=>setAnalytics(r.data)).catch(()=>{});
    A.academyPolicies().then(r=>setPolicies(r.data)).catch(()=>{});
  }, []);
  if (!s) return <div>Loading…</div>;
  return (
    <div data-testid="qa-center-page">
      <H1>Quality Assurance & Accreditation-Readiness</H1>
      <Sub>Internal self-audit. This report reflects preparation to accreditation standards; it is NOT a grant of accreditation.</Sub>
      <div className="banner-warning mb-4">{s.status_note}</div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="qa-counts">
        {[
          ["Programs", s.counts.programs],
          ["Courses", s.counts.courses],
          ["Lessons", s.counts.lessons],
          ["Learners", s.counts.learners_with_progress],
          ["Completions", s.counts.total_lesson_completions],
          ["Transcripts", s.counts.transcripts_issued],
          ["Revoked", s.counts.transcripts_revoked],
          ["Integrity cases", s.counts.integrity_cases],
        ].map(([k,v],i) => (
          <Card key={i} className="text-center">
            <div className="label-caps text-xs">{k}</div>
            <div className="font-display text-2xl">{v}</div>
          </Card>
        ))}
      </div>

      <H2>Curriculum map</H2>
      <div className="overflow-x-auto mb-6"><table className="text-sm w-full" data-testid="qa-curriculum-map">
        <thead><tr className="text-left">
          <th className="py-1 pr-2 label-caps">Program</th>
          <th className="py-1 pr-2 label-caps">Credential</th>
          <th className="py-1 pr-2 label-caps">Courses</th>
          <th className="py-1 pr-2 label-caps">Lessons</th>
          <th className="py-1 pr-2 label-caps">Avg. pass %</th>
          <th className="py-1 pr-2 label-caps">Oldest source review</th>
        </tr></thead>
        <tbody>{s.curriculum_map.map((p,i) => (
          <tr key={p.program_id} className="border-t" style={{borderColor:'var(--border)'}}>
            <td className="py-1 pr-2">{p.program_title}</td>
            <td className="py-1 pr-2 text-xs" style={{color:'var(--muted)'}}>{p.credential}</td>
            <td className="py-1 pr-2 font-mono text-xs">{p.courses}</td>
            <td className="py-1 pr-2 font-mono text-xs">{p.lessons}</td>
            <td className="py-1 pr-2 font-mono text-xs">{p.avg_passing_score}%</td>
            <td className="py-1 pr-2 font-mono text-xs">{p.oldest_source_review || '—'}</td>
          </tr>
        ))}</tbody>
      </table></div>

      {s.findings.length > 0 && (
        <div className="mb-6" data-testid="qa-findings">
          <H2>Findings</H2>
          {s.findings.map((f,i) => (
            <div key={i} className={`card p-3 mb-2 ${f.severity==='warning'?'border-amber-700':''}`}>
              <span className={`chip text-xs mr-2 ${f.severity==='warning'?'chip-amber':'chip-gray'}`}>{f.severity}</span>
              <span className="text-sm">{f.message}</span>
            </div>
          ))}
        </div>
      )}

      {analytics && (
        <div data-testid="qa-analytics">
          <H2>Enrollment & completion analytics</H2>
          <div className="overflow-x-auto"><table className="text-sm w-full">
            <thead><tr className="text-left">
              <th className="py-1 pr-2 label-caps">Course</th>
              <th className="py-1 pr-2 label-caps">Started</th>
              <th className="py-1 pr-2 label-caps">Completed</th>
              <th className="py-1 pr-2 label-caps">Completion rate</th>
            </tr></thead>
            <tbody>{analytics.courses.map(c => (
              <tr key={c.course_id} className="border-t" style={{borderColor:'var(--border)'}}>
                <td className="py-1 pr-2">{c.title}</td>
                <td className="py-1 pr-2 font-mono text-xs">{c.learners_started}</td>
                <td className="py-1 pr-2 font-mono text-xs">{c.learners_completed}</td>
                <td className="py-1 pr-2 font-mono text-xs">{c.completion_rate}%</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      {policies.length > 0 && (
        <div className="mt-8" data-testid="qa-policies">
          <H2>Institutional Policies ({policies.length})</H2>
          <div className="text-sm mb-3" style={{color:'var(--muted)'}}>Governing policy documents from the Academy Institutional Package v1.0.</div>
          <div className="grid md:grid-cols-2 gap-2">
            {policies.map(p => (
              <button key={p.id} onClick={()=>setOpenPolicy(p)} className="card p-3 text-left hover:-translate-y-0.5 transition-transform" data-testid={`policy-${p.id}`}>
                <div className="font-display text-sm">{p.title}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>{p.filename}</div>
              </button>
            ))}
          </div>
          {openPolicy && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={()=>setOpenPolicy(null)} data-testid="policy-modal">
              <div className="bg-white max-w-4xl max-h-[90vh] w-full overflow-y-auto rounded p-6" style={{background:'var(--surface)'}} onClick={e=>e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="label-caps text-xs">{openPolicy.filename}</div>
                    <H2>{openPolicy.title}</H2>
                  </div>
                  <button className="btn-outline text-xs" onClick={()=>setOpenPolicy(null)} data-testid="policy-close">Close</button>
                </div>
                <div className="prose max-w-none" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(openPolicy.html)}}/>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ==================== TEACHING GENERATOR (admin) ====================
export function TeachingGeneratorPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);

  const refresh = () => {
    A.academyTeaching.status().then((r) => setStatus(r.data)).catch((e) => {
      if (e?.response?.status === 403) toast.error("Admins only");
    });
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await A.academyTeaching.start(force);
      toast.success("Teaching generation started — this runs in the background.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div>Loading…</div>;
  const pct = status.total ? Math.round((status.processed / status.total) * 100) : 0;
  const done = status.processed - status.skipped - status.failed;

  return (
    <div data-testid="teaching-generator-page">
      <H1>Academy · Teaching Generator</H1>
      <Sub>
        Rebuild every lesson body with true, living educational content — concept, mechanics,
        worked example, why-it-matters, common pitfalls, applied scenario, and (for foundation
        tracks) a decision framework. Foundation programs get the deep 1,500-2,500-word treatment;
        advanced/practicum tracks get the standard 600-900-word treatment. The existing operating
        framework is preserved — teaching content renders before it.
      </Sub>

      <Card className="mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" className="w-4" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="teaching-force" />
              Force regenerate (overwrite existing teaching)
            </label>
          </div>
          <button className="btn-gold" onClick={start} disabled={busy || status.running} data-testid="start-teaching-btn">
            {status.running ? "Job running…" : busy ? "Starting…" : (force ? "Regenerate ALL lessons" : "Generate missing lessons")}
          </button>
        </div>
        <div className="text-xs mt-2" style={{color:'var(--muted)'}}>
          Target teaching version: v{status.target_version}. Estimated cost: 1 Claude Sonnet 4.6 call per lesson (~2-4k tokens each).
          Runs in the background — you can leave this page open.
        </div>
      </Card>

      <Card className="mb-4" data-testid="teaching-status-card">
        <div className="flex items-center justify-between mb-2">
          <div className="label-caps">Current job</div>
          <span className={`chip ${status.running ? "chip-amber" : "chip-green"}`}>
            {status.running ? "RUNNING" : "IDLE"}
          </span>
        </div>
        <ProgressBar pct={pct} />
        <div className="grid md:grid-cols-4 gap-3 mt-3 text-sm">
          <div><div className="label-caps">Progress</div><div>{status.processed} / {status.total} ({pct}%)</div></div>
          <div><div className="label-caps">Done</div><div style={{color:'var(--good)'}}>{done}</div></div>
          <div><div className="label-caps">Skipped</div><div style={{color:'var(--muted)'}}>{status.skipped}</div></div>
          <div><div className="label-caps">Failed</div><div style={{color:'var(--overdue)'}}>{status.failed}</div></div>
        </div>
        {status.current && (
          <div className="text-xs mt-2" style={{color:'var(--muted)'}} data-testid="teaching-current">
            Now writing: <em>{status.current}</em>
          </div>
        )}
        {status.started_at && (
          <div className="text-xs mt-1" style={{color:'var(--muted)'}}>
            Started {new Date(status.started_at).toLocaleString()} by {status.started_by}
            {status.finished_at && ` · finished ${new Date(status.finished_at).toLocaleString()}`}
          </div>
        )}
      </Card>

      {status.errors && status.errors.length > 0 && (
        <Card>
          <div className="label-caps mb-2" style={{color:'var(--overdue)'}}>Recent errors ({status.errors.length})</div>
          <div className="space-y-1 text-xs">
            {status.errors.slice().reverse().map((e, i) => (
              <div key={i} className="p-2 rounded bg-[color:var(--surface-2)]">
                <div className="font-medium">{e.lesson}</div>
                <div style={{color:'var(--muted)'}}>{e.error}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
